import React, { useState } from "react"
import { useForm } from "react-hook-form"
import { firestore } from "../../core"
import * as FirebaseAuth from "firebase/auth"
import * as FS from "firebase/firestore"
import { UserDocument, UserId, FirestorePaths, UserDeviceDocuments, DeviceId, UserDeviceDocument } from "popopo-msg"
import { Button, Spinner, Form } from "react-bootstrap"
import { useToast } from "popopo-lib-front/hook"
import { generateUuidV4 } from "popopo-lib-front"

type CreateDeviceForm = {
  device_id: string
  platform: "web" | "ios" | "android"
  model: string
}

export const UserDeviceDebug = (props: {
  firebase: FirebaseAuth.User
  user: UserDocument
  userDevices: UserDeviceDocument[]
}) => {
  const { userDevices } = props
  const toast = useToast()
  const [activatingDeviceId, setActivatingDeviceId] = useState<string | null>(null)

  const createForm = useForm<CreateDeviceForm>({
    mode: "onBlur",
    defaultValues: {
      device_id: generateUuidV4(),
      platform: "web",
      model: "Web Browser",
    },
  })
  const { isSubmitting, isValid } = createForm.formState

  const handleCreateUserDevice = async (values: CreateDeviceForm) => {
    try {
      const now = Date.now()
      const deviceDocRef = FS.doc(
        firestore,
        FirestorePaths.UserPrivates.UserDevices.getDocPath(props.user.id as UserId, values.device_id as DeviceId)
      )

      const deviceSnapshot = await FS.getDoc(deviceDocRef)
      if (deviceSnapshot.exists()) {
        throw new Error("Device already exists. Please use a different device ID.")
      }

      await FS.setDoc(deviceDocRef, UserDeviceDocuments.toDocumentData({
        platform: values.platform,
        device_id: values.device_id as DeviceId,
        last_active_at: now,
        model: values.model,
        created_at: now,
        updated_at: now,
      }))

      toast.add({ type: "success", title: "User Device", message: `Device ${values.device_id} created and set as active!` })
      createForm.reset({
        device_id: generateUuidV4(),
        platform: "web",
        model: "Web Browser",
      })
    } catch (e) {
      console.error(e)
      const errorMessage = e instanceof Error ? e.message : String(e)
      toast.add({ type: "danger", title: "User Device", message: `Failed to create device: ${errorMessage}` })
    }
  }

  const handleMakeDeviceActive = async (deviceId: DeviceId) => {
    setActivatingDeviceId(deviceId)
    try {
      const now = Date.now()
      const deviceDocRef = FS.doc(
        firestore,
        FirestorePaths.UserPrivates.UserDevices.getDocPath(props.user.id as UserId, deviceId)
      )

      await FS.updateDoc(deviceDocRef, {
        last_active_at: now,
        updated_at: now,
      })

      toast.add({ type: "success", title: "User Device", message: `Active device updated to ${deviceId}!` })
    } catch (e) {
      console.error(e)
      const errorMessage = e instanceof Error ? e.message : String(e)
      toast.add({ type: "danger", title: "User Device", message: `Failed to update active device: ${errorMessage}` })
    } finally {
      setActivatingDeviceId(null)
    }
  }

  // Find the device with the highest (most recent) last_active_at
  const activeDeviceId = 0 < userDevices.length
    ? userDevices.reduce((prev, current) =>
        prev.last_active_at < current.last_active_at ? current : prev
      ).device_id
    : null

  return (
    <div className="d-flex flex-column gap-3 w-100">
      <h4>Debug: User Devices</h4>

      {/* Display Device List from user-devices subcollection */}
      <div className="card">
        <div className="card-header">
          <strong>Device List (from user-devices subcollection)</strong>
        </div>
        <div className="card-body">
          <p className="text-muted small mb-3">
            Note: The device with the highest <code>last_active_at</code> is considered active.
          </p>
          {userDevices.length === 0 ? (
            <p className="text-muted">No devices registered</p>
          ) : (
            <ul className="list-group">
              {userDevices.map((device) => {
                const isActive = device.device_id === activeDeviceId
                const isActivating = activatingDeviceId === device.device_id
                return (
                  <li key={device.device_id} className={`list-group-item ${isActive ? "list-group-item-success" : ""}`}>
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <p className="mb-1">
                          <strong>Device ID:</strong> {device.device_id}
                          {isActive && <span className="badge bg-success ms-2">Active</span>}
                        </p>
                        <p className="mb-1"><strong>Platform:</strong> {device.platform}</p>
                        <p className="mb-1"><strong>Model:</strong> {device.model || "N/A"}</p>
                        <p className="mb-1"><strong>Last Active At:</strong> {new Date(device.last_active_at).toLocaleString()}</p>
                        <p className="mb-1"><strong>Created At:</strong> {new Date(device.created_at).toLocaleString()}</p>
                        <p className="mb-0"><strong>Updated At:</strong> {new Date(device.updated_at).toLocaleString()}</p>
                      </div>
                      <div>
                        <Button
                          variant={isActive ? "secondary" : "warning"}
                          size="sm"
                          disabled={isActive || isActivating}
                          onClick={() => handleMakeDeviceActive(device.device_id as DeviceId)}
                        >
                          {isActivating ? <Spinner animation="border" size="sm" /> : isActive ? "Active" : "Make Active"}
                        </Button>
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Create User Device Form */}
      <div className="card">
        <div className="card-header">
          <strong>Create User Device (and make it active)</strong>
        </div>
        <div className="card-body">
          <p className="text-muted small mb-3">
            Note: In production, device IDs should be generated once and persisted locally (Web: localStorage, iOS: Keychain, Android: SharedPreferences).
          </p>
          <form className="d-flex flex-column gap-3" onSubmit={createForm.handleSubmit(handleCreateUserDevice)}>
            <Form.Group>
              <Form.Label>Device ID (UUID v4)</Form.Label>
              <Form.Control
                type="text"
                {...createForm.register("device_id", { required: true })}
                disabled={isSubmitting}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Platform</Form.Label>
              <Form.Select
                {...createForm.register("platform")}
                disabled={isSubmitting}
              >
                <option value="web">Web</option>
                <option value="ios">iOS</option>
                <option value="android">Android</option>
              </Form.Select>
            </Form.Group>

            <Form.Group>
              <Form.Label>Device Model</Form.Label>
              <Form.Control
                type="text"
                {...createForm.register("model")}
                disabled={isSubmitting}
              />
            </Form.Group>

            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || !isValid}
            >
              {isSubmitting ? <Spinner animation="border" size="sm" /> : "Create Device"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}
